import React, { useState, useEffect } from "react";
import {
  X,
  Bell,
  CheckCircle,
  AlertCircle,
  Info,
  Calendar,
  Trash2,
} from "lucide-react";
import { Button } from "./ui/Button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  clearAllNotifications,
  Notification,
} from "../services/notification";

interface NotificationSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

const NotificationSidebar: React.FC<NotificationSidebarProps> = ({
  isOpen,
  onClose,
  onUpdate,
}) => {
  const [notifications, setNotifications] = React.useState<Notification[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const [perPage, setPerPage] = React.useState(15);
  const [filterType, setFilterType] = React.useState<"all" | "unread" | "read">("all");

  const fetchNotifications = async (page: number = 1, type: "all" | "unread" | "read" = "all", isLoadMore: boolean = false, currentPerPage: number = perPage) => {
    try {
      if (isLoadMore) setLoadingMore(true);
      else setLoading(true);

      const [notifResponse, countResponse] = await Promise.all([
        getNotifications(page, currentPerPage, type),
        getUnreadCount()
      ]);

      if (notifResponse.success) {
        if (isLoadMore) {
          setNotifications(prev => [...prev, ...notifResponse.data.notifications]);
        } else {
          setNotifications(notifResponse.data.notifications);
        }
        setTotalPages(notifResponse.data.pagination.last_page);
        setCurrentPage(notifResponse.data.pagination.current_page);
      }

      if (countResponse.success) {
        setUnreadCount(countResponse.data.unread_count);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      fetchNotifications(1, filterType, false, perPage);
    }
  }, [isOpen, filterType, perPage]);

  const handleLoadMore = () => {
    if (currentPage < totalPages) {
      fetchNotifications(currentPage + 1, filterType, true, perPage);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "success":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "warning":
        return <AlertCircle className="w-5 h-5 text-orange-400" />;
      case "info":
        return <Info className="w-5 h-5 text-blue-500" />;
      case "booking":
        return <Calendar className="w-5 h-5 text-[#885ABB]" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  // Actions
  const handleMarkAllRead = async () => {
    try {
      await markAllAsRead();
      setNotifications(notifications.map((n) => ({ ...n, read_at: new Date().toISOString() })));
      setUnreadCount(0);
      onUpdate?.();
      window.dispatchEvent(new Event("notificationsUpdated"));
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const handleClearAll = async () => {
    try {
      await clearAllNotifications();
      setNotifications([]);
      setUnreadCount(0);
      onUpdate?.();
      window.dispatchEvent(new Event("notificationsUpdated"));
    } catch (error) {
      console.error("Failed to clear all notifications:", error);
    }
  };

  const handleDeleteOne = async (id: string | number) => {
    try {
      await deleteNotification(id);
      const updated = notifications.filter((n) => n.id !== id);
      setNotifications(updated);
      setUnreadCount(updated.filter((n) => !n.read_at).length);
      onUpdate?.();
      window.dispatchEvent(new Event("notificationsUpdated"));
    } catch (error) {
      console.error("Failed to delete notification:", error);
    }
  };

  const markOneRead = async (id: string | number, read_at: string | null) => {
    if (read_at) return;
    try {
      await markAsRead(id);
      const updated = notifications.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
      setNotifications(updated);
      setUnreadCount(updated.filter((n) => !n.read_at).length);
      onUpdate?.();
      window.dispatchEvent(new Event("notificationsUpdated"));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/30 backdrop-blur-sm z-[120] transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        onClick={onClose}
      />

      {/* Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[600px] bg-white shadow-2xl z-[130] transform transition-transform duration-300 ease-out flex flex-col ${isOpen ? "translate-x-0" : "translate-x-full"
          }`}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-[#885ABB] shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold flex items-center gap-2 text-white">
                <Bell className="w-6 h-6 text-white" /> Notifications
              </h2>
              <p className="text-sm text-white/80 mt-1">
                {unreadCount} unread{" "}
                {unreadCount === 1 ? "notification" : "notifications"}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-md"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          <div className="space-y-4 mt-4">
            <div className="flex bg-white/10 p-1 rounded-lg">
              <button
                onClick={() => setFilterType("all")}
                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${filterType === "all" ? "bg-white text-[#885ABB]" : "text-white hover:bg-white/5"
                  }`}
              >
                All
              </button>
              <button
                onClick={() => setFilterType("unread")}
                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${filterType === "unread" ? "bg-white text-[#885ABB]" : "text-white hover:bg-white/5"
                  }`}
              >
                Unread
              </button>
              <button
                onClick={() => setFilterType("read")}
                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${filterType === "read" ? "bg-white text-[#885ABB]" : "text-white hover:bg-white/5"
                  }`}
              >
                Read
              </button>
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-white/70">Show:</span>
                <Select
                  value={perPage.toString()}
                  onValueChange={(value) => setPerPage(Number(value))}
                >
                  <SelectTrigger className="w-[70px] h-8 bg-white text-[#885ABB] border-transparent text-xs font-semibold focus:ring-white/40 hover:bg-white/90">
                    <SelectValue placeholder="15" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3</SelectItem>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="15">15</SelectItem>
                    <SelectItem value="30">30</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 flex-1">
                <button
                  onClick={handleMarkAllRead}
                  disabled={unreadCount === 0}
                  className="flex-1 bg-white text-[#885ABB] font-medium transition px-3 py-1.5 rounded-lg text-[13px] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Mark all read
                </button>
                <button
                  onClick={handleClearAll}
                  disabled={notifications.length === 0}
                  className="flex-1 bg-white text-gray-700 font-medium transition px-3 py-1.5 rounded-lg text-[13px] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Clear all
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto p-4 pb-20 custom-scrollbar relative">
          {loading && (
            <div className={`flex items-center justify-center ${notifications.length > 0 ? "absolute inset-0 bg-white/60 z-10" : "h-40"}`}>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#885ABB]"></div>
            </div>
          )}

          {!loading && notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 p-8">
              <Bell className="w-16 h-16 mb-4 opacity-30" />
              <p className="text-lg font-medium">No notifications</p>
              <p className="text-sm">You're all caught up!</p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {notifications.map((n) => (
                  <div
                    key={n.id}
                    onClick={() => markOneRead(n.id, n.read_at)}
                    className={`group relative rounded-md p-4 border  cursor-pointer flex items-start gap-3 ${!n.read_at
                      ? "border-[#885ABB]/40 bg-white "
                      : "border-gray-200 bg-white"
                      }`}
                  >
                    {/* Left border for unread */}
                    {!n.read_at && (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#885ABB] rounded-l-xl" />
                    )}

                    <div className="flex-shrink-0 mt-1">
                      <div className="bg-white p-2 rounded-lg border border-gray-200 shadow-sm">
                        {getNotificationIcon(n.data?.type?.toLowerCase() || n.type)}
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3
                        className={`text-sm mb-1 ${!n.read_at ? "font-semibold" : "font-normal"
                          } text-gray-800`}
                      >
                        {n.data?.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">{n.data?.message}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-400 font-medium">
                          {n.created_at_human}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteOne(n.id);
                          }}
                          className="opacity-100  p-1.5 rounded-md"
                        >
                          <Trash2 className="w-4 h-4 text-red-400 hover:text-red-500" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {currentPage < totalPages && (
                <div className="mt-6 pb-4">
                  <Button
                    variant="outline"
                    className="w-full border-gray-200 text-gray-600 hover:bg-gray-50 flex items-center justify-center gap-2"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#885ABB]"></div>
                    ) : (
                      "Load More"
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Custom Scrollbar */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #885ABB;
          border-radius: 10px;
        }
      `}</style>
    </>
  );
};

export default NotificationSidebar;
